<script setup lang="ts">
import { computed } from 'vue'
import { VBadge, VTable } from '@/modules/shared/components'
import type { MessageLog } from '../types'

const props = withDefaults(
  defineProps<{
    logs: MessageLog[]
    percentageOpened: number
    compact?: boolean
  }>(),
  { compact: false },
)

const summary = computed(() => {
  const total = props.logs.length
  const delivered = props.logs.filter((l) => l.status === 'delivered').length
  const opened = props.logs.filter((l) => l.status === 'opened').length
  const queued = props.logs.filter((l) => l.status === 'queued').length
  const dropped = props.logs.filter((l) => l.status === 'dropped').length
  return { total, delivered, opened, queued, dropped }
})

const statusColor = (status: string) => {
  switch (status) {
    case 'queued':
      return 'amber'
    case 'delivered':
      return 'sky'
    case 'opened':
      return 'green'
    case 'dropped':
      return 'red'
    default:
      return 'gray'
  }
}

const columns = [
  { key: 'name', name: 'Recipient', type: 'string', align: 'left', is_visible: true, fixed: true },
  { key: 'email', name: 'Email', type: 'string', align: 'left', is_visible: true },
  { key: 'status', name: 'Status', type: 'string', align: 'left', is_visible: true },
  { key: 'created_at', name: 'Date', type: 'date', align: 'left', is_visible: true },
]
</script>

<template>
  <!-- Compact sidebar mode -->
  <div v-if="compact" class="space-y-3">
    <div class="flex flex-wrap gap-2">
      <VBadge color="gray" size="xxs">{{ summary.total }} total</VBadge>
      <VBadge color="green" size="xxs">{{ summary.opened }} opened</VBadge>
      <VBadge color="sky" size="xxs">{{ summary.delivered }} delivered</VBadge>
      <VBadge v-if="summary.queued > 0" color="amber" size="xxs">{{ summary.queued }} queued</VBadge>
      <VBadge v-if="summary.dropped > 0" color="red" size="xxs">{{ summary.dropped }} dropped</VBadge>
    </div>

    <ul class="max-h-[32rem] divide-y divide-gray-100 overflow-y-auto">
      <li v-for="log in logs" :key="log.id" class="flex items-center justify-between py-2">
        <span class="mr-3 truncate text-sm text-gray-700" :title="log.email">
          {{ log.name || log.email }}
        </span>
        <VBadge :color="statusColor(log.status)" size="xxs" class="shrink-0 capitalize">
          {{ log.status }}
        </VBadge>
      </li>
    </ul>
  </div>

  <!-- Full table mode -->
  <div v-else class="space-y-4">
    <div class="flex flex-wrap gap-3">
      <div class="rounded-md bg-gray-50 px-3 py-2 text-sm">
        <span class="text-gray-500">Total:</span> <span class="font-medium">{{ summary.total }}</span>
      </div>
      <div class="rounded-md bg-green-50 px-3 py-2 text-sm">
        <span class="text-gray-500">Opened:</span> <span class="font-medium">{{ summary.opened }}</span>
        <span v-if="summary.total" class="text-gray-400"> ({{ percentageOpened }}%)</span>
      </div>
      <div class="rounded-md bg-sky-50 px-3 py-2 text-sm">
        <span class="text-gray-500">Delivered:</span> <span class="font-medium">{{ summary.delivered }}</span>
      </div>
      <div class="rounded-md bg-amber-50 px-3 py-2 text-sm">
        <span class="text-gray-500">Queued:</span> <span class="font-medium">{{ summary.queued }}</span>
      </div>
      <div class="rounded-md bg-red-50 px-3 py-2 text-sm">
        <span class="text-gray-500">Dropped:</span> <span class="font-medium">{{ summary.dropped }}</span>
      </div>
    </div>

    <VTable name="delivery-status" :columns="columns" :items="logs" :skeleton="false" :slots="['status']">
      <template #status="{ item }">
        <VBadge :color="statusColor(item.status)">{{ item.status }}</VBadge>
      </template>
    </VTable>
  </div>
</template>
